/**
 * Passwort zuruecksetzen, Schritt 2: neues Passwort setzen.
 *
 * Der Link aus der Mail bringt einen Einmal-Code in der Adresse mit. Der
 * Supabase-Client loest ihn beim Start ein (`detectSessionInUrl`) und erzeugt
 * eine Sitzung; erst damit darf `updateUser` das Passwort aendern.
 *
 * WARUM DIE SEITE AUF DIE SITZUNG WARTET: Die Einloesung ist asynchron. Wer
 * sofort „kein gueltiger Link" anzeigt, zeigt es jedem — auch dem, dessen Link
 * eine Zehntelsekunde spaeter greift.
 *
 * WARUM DANACH ABGEMELDET WIRD: Die Sitzung aus einem Wiederherstellungslink
 * ist ein Nachweis fuer genau einen Vorgang. Wer sein Passwort geaendert hat,
 * soll sich einmal damit anmelden — das ist zugleich die Probe, dass es
 * angekommen ist.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Feld, Hinweis, Knopf, Rahmen, Verweis } from '../ui';
import { fehlertext, supabase } from '../supabase';
import { useSitzung } from '../session';
import { t } from '../sprache';

const MIN_ZEICHEN = 8;

export default function PasswortNeu() {
  const { status } = useSitzung();
  const navigate = useNavigate();
  const [passwort, setPasswort] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    if (passwort.length < MIN_ZEICHEN) {
      setFehler(t(`Das Passwort braucht mindestens ${MIN_ZEICHEN} Zeichen.`, `The password needs at least ${MIN_ZEICHEN} characters.`));
      return;
    }
    setLaedt(true);
    const { error } = await supabase.auth.updateUser({ password: passwort });
    if (error) {
      setLaedt(false);
      setFehler(fehlertext(error));
      return;
    }
    await supabase.auth.signOut();
    setLaedt(false);
    navigate('/login?geaendert=1', { replace: true });
  }

  if (status === 'laedt') {
    return <Rahmen titel={t('Einen Moment', 'One moment')}><Hinweis>{t('Der Link wird geprüft.', 'Checking the link.')}</Hinweis></Rahmen>;
  }

  if (status === 'abgemeldet') {
    return (
      <Rahmen
        titel={t('Der Link trägt nicht mehr', 'The link no longer works')}
        unter={t('Wiederherstellungs-Links gelten eine Stunde und lassen sich nur einmal benutzen.', 'Reset links are valid for one hour and can only be used once.')}
        fuss={<Verweis to="/login">{t('Zurück zur Anmeldung', 'Back to sign in')}</Verweis>}
      >
        <Hinweis art="schlecht">{t('Fordere einen neuen Link an, dann geht es weiter.', 'Request a new link to continue.')}</Hinweis>
        <Knopf type="button" onClick={() => navigate('/password')}>{t('Neuen Link anfordern', 'Request a new link')}</Knopf>
      </Rahmen>
    );
  }

  return (
    <Rahmen titel={t('Neues Passwort setzen', 'Set a new password')} unter={t('Danach meldest du dich einmal damit an.', 'Then you sign in with it once.')}>
      <form onSubmit={absenden} noValidate>
        {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
        <Feld
          label={t('Neues Passwort', 'New password')} type="password" name="new-password" autoComplete="new-password" required
          hinweis={t(`Mindestens ${MIN_ZEICHEN} Zeichen.`, `At least ${MIN_ZEICHEN} characters.`)}
          value={passwort} onChange={e => setPasswort(e.target.value)} disabled={laedt}
        />
        <Knopf type="submit" laedt={laedt}>{t('Passwort speichern', 'Save password')}</Knopf>
      </form>
    </Rahmen>
  );
}
